import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { useLocation, useNavigate } from "react-router-dom";
import NavBar from "../Common/NavBar";
import { CinturonesSeguridadEventosURL, VehiculosURL } from "../API/apiurls";
import { useGlobalState } from "../Context/GlobalStateContext";

const PAGE_SIZE = 15;

const formatDateTime = (value) => {
  if (!value) return "Sin datos";
  const date = Array.isArray(value)
    ? new Date(
        Number(value[0]),
        Number(value[1] || 1) - 1,
        Number(value[2] || 1),
        Number(value[3] || 0),
        Number(value[4] || 0),
        Number(value[5] || 0),
        Math.floor(Number(value[6] || 0) / 1000000)
      )
    : new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin datos";
  return new Intl.DateTimeFormat("es-PE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "America/Lima",
  }).format(date);
};

const toDate = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) {
    const date = new Date(
      Number(value[0]),
      Number(value[1] || 1) - 1,
      Number(value[2] || 1),
      Number(value[3] || 0),
      Number(value[4] || 0),
      Number(value[5] || 0),
      Math.floor(Number(value[6] || 0) / 1000000)
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "string" && /^\d{2}\/\d{2}\/\d{4}/.test(value)) {
    const [datePart, timePart = "00:00:00"] = value.split(" ");
    const [day, month, year] = datePart.split("/").map(Number);
    const [hour = 0, minute = 0, second = 0] = timePart.split(":").map(Number);
    const date = new Date(year, month - 1, day, hour, minute, second);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeSeverity = (value) => {
  const severity = String(value || "ADVERTENCIA").trim().toUpperCase();
  if (severity === "CRITICO") return "CRITICO";
  if (severity === "OK") return "OK";
  return "ADVERTENCIA";
};

const normalizeEstado = (value) => String(value || "").trim().toUpperCase();

const isAbrochado = (item) => normalizeEstado(item?.estado) === "ABROCHADO";

const eventTime = (item) => toDate(item?.fechaHora)?.getTime() || 0;


const getLat = (item) => item?.latitud ?? item?.lat ?? null;
const getLng = (item) => item?.longitud ?? item?.lng ?? item?.lon ?? null;

const formatCoordinate = (lat, lng) => {
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return "Sin coordenada";
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
};

const mapsUrl = (lat, lng) => {
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return `https://www.google.com/maps?q=${latitude},${longitude}`;
};

const calculateDistanceMeters = (startLat, startLng, endLat, endLng) => {
  const lat1 = Number(startLat);
  const lng1 = Number(startLng);
  const lat2 = Number(endLat);
  const lng2 = Number(endLng);
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return null;

  const toRad = (value) => (value * Math.PI) / 180;
  const earthRadiusMeters = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return earthRadiusMeters * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const formatDuration = (startAt, endAt) => {
  if (!startAt) return "Sin datos";
  const end = endAt || new Date();
  const totalSeconds = Math.max(0, Math.floor((end.getTime() - startAt.getTime()) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const eventKey = (item) => {
  const vehicle = item?.vehiculoId || item?.vehiculoNombre || "vehiculo";
  const seat = item?.cinturonId || item?.cinturonSerialNumero || item?.cinturonCodigo || item?.asiento || "asiento";
  return `${vehicle}-${String(seat).trim().toUpperCase()}`;
};

const buildAlertIntervals = (events) => {
  const ordered = [...events].sort((a, b) => eventTime(a) - eventTime(b));
  const openByKey = new Map();
  const intervals = [];

  ordered.forEach((event) => {
    const key = eventKey(event);
    if (isAbrochado(event)) {
      const open = openByKey.get(key);
      if (!open) return;
      const endAt = toDate(event.fechaHora);
      const endLat = getLat(event);
      const endLng = getLng(event);
      const distanceMeters = calculateDistanceMeters(open.startLat, open.startLng, endLat, endLng);
      intervals.push({
        ...open,
        endAt,
        endEvent: event,
        endLat,
        endLng,
        durationLabel: formatDuration(open.startAt, endAt),
        distanceMeters,
        distanceLabel: formatDistance(distanceMeters),
      });
      openByKey.delete(key);
      return;
    }

    if (!openByKey.has(key)) {
      openByKey.set(key, eventToAlertRow(event));
    }
  });

  openByKey.forEach((open) => {
    intervals.push({
      ...open,
      durationLabel: formatDuration(open.startAt, null),
      distanceLabel: formatDistance(open.distanceMeters),
    });
  });

  return intervals.sort((a, b) => (b.startAt?.getTime() || 0) - (a.startAt?.getTime() || 0));
};


const formatDistance = (meters) => {
  const value = Number(meters);
  if (!Number.isFinite(value)) return "Sin datos";
  if (value >= 1000) return `${(value / 1000).toFixed(2)} km`;
  return `${Math.round(value)} m`;
};

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const resolvePeriodRange = (periodo, inicio, fin) => {
  const today = new Date();
  const start = new Date(today);
  const end = new Date(today);

  if (periodo === "SEMANA") start.setDate(today.getDate() - 6);
  if (periodo === "MES") start.setMonth(today.getMonth() - 1);
  if (periodo === "PERSONALIZADO") {
    const customStart = inicio ? new Date(`${inicio}T00:00:00`) : null;
    const customEnd = fin ? new Date(`${fin}T23:59:59`) : null;
    return {
      start: customStart && !Number.isNaN(customStart.getTime()) ? customStart : null,
      end: customEnd && !Number.isNaN(customEnd.getTime()) ? customEnd : null,
    };
  }

  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};


const resolveSeverityFromEvent = (item) => {
  if (isAbrochado(item)) return "OK";
  return item?.vehiculoEnMarcha ? "CRITICO" : "ADVERTENCIA";
};

const eventToAlertRow = (item) => ({
  id: item?.id || eventTime(item),
  vehiculoId: item?.vehiculoId,
  vehiculoNombre: item?.vehiculoNombre,
  asiento: item?.asiento,
  estado: item?.estado || "DESABROCHADO",
  severidad: resolveSeverityFromEvent(item),
  startAt: toDate(item?.fechaHora),
  endAt: null,
  startEvent: item,
  endEvent: null,
  startLat: getLat(item),
  startLng: getLng(item),
  endLat: getLat(item),
  endLng: getLng(item),
  durationLabel: "Evento",
  distanceLabel: "Sin datos",
  distanceMeters: null,
  detalle: item?.detalle || item?.alerta || "Alerta registrada",
});


export function CinturonesSeguridadAlertas() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useGlobalState();
  const empresaId = Number(userData?.empresaId || localStorage.getItem("empresaid") || 0) || undefined;
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const vehiculoId = params.get("vehiculoId") || "";

  const [vehiculoNombre, setVehiculoNombre] = useState("");
  const [alertas, setAlertas] = useState([]);
  const [page, setPage] = useState(1);
  const [loadingAlertas, setLoadingAlertas] = useState(false);
  const [estadoFiltro, setEstadoFiltro] = useState("TODOS");
  const [severidadFiltro, setSeveridadFiltro] = useState("TODAS");
  const [busqueda, setBusqueda] = useState("");
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("XLSX");
  const [downloadPeriod, setDownloadPeriod] = useState("HOY");
  const [downloadStartDate, setDownloadStartDate] = useState(toDateInputValue(new Date()));
  const [downloadEndDate, setDownloadEndDate] = useState(toDateInputValue(new Date()));

  useEffect(() => {
    if (!empresaId || !vehiculoId) return;
    const loadVehiculo = async () => {
      try {
        const response = await axios.get(`${VehiculosURL}/xempresa/${empresaId}`);
        const items = Array.isArray(response.data) ? response.data : [];
        const current = items.find((item) => String(item.id) === String(vehiculoId));
        setVehiculoNombre(current?.nombre || "");
      } catch (error) {
        console.error("Error cargando vehículo:", error);
        setVehiculoNombre("");
      }
    };
    loadVehiculo();
  }, [empresaId, vehiculoId]);

  const loadAlertas = useCallback(async () => {
    if (!empresaId) return;
    setLoadingAlertas(true);
    try {
      const query = { empresaId };
      if (vehiculoId) query.vehiculoId = Number(vehiculoId);
      const response = await axios.get(CinturonesSeguridadEventosURL, { params: query });
      const payload = response.data || {};
      const items = Array.isArray(payload) ? payload : Array.isArray(payload.content) ? payload.content : [];
      setAlertas(buildAlertIntervals(items));
      setPage(1);
    } catch (error) {
      console.error("Error cargando alertas:", error);
      setAlertas([]);
    } finally {
      setLoadingAlertas(false);
    }
  }, [empresaId, vehiculoId]);

  useEffect(() => {
    loadAlertas();
  }, [loadAlertas]);

  const alertasFiltradas = useMemo(() => {
    const search = busqueda.trim().toUpperCase();
    return alertas.filter((item) => {
      if (estadoFiltro !== "TODOS" && normalizeEstado(item.estado) !== estadoFiltro) return false;
      if (severidadFiltro !== "TODAS" && normalizeSeverity(item.severidad) !== severidadFiltro) return false;
      if (!search) return true;
      const haystack = [item.vehiculoNombre, item.asiento, item.estado, item.severidad, item.detalle]
        .filter(Boolean)
        .join(" ")
        .toUpperCase();
      return haystack.includes(search);
    });
  }, [alertas, busqueda, estadoFiltro, severidadFiltro]);

  useEffect(() => {
    setPage(1);
  }, [busqueda, estadoFiltro, severidadFiltro]);

  const totalPages = Math.max(1, Math.ceil(alertasFiltradas.length / PAGE_SIZE));
  const eventTotal = alertasFiltradas.length;
  const safePage = Math.min(page, totalPages);
  const visibleRows = alertasFiltradas.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);


  const exportRows = useMemo(() => {
    const { start, end } = resolvePeriodRange(downloadPeriod, downloadStartDate, downloadEndDate);
    return alertasFiltradas.filter((item) => {
      const date = item.startAt;
      if (!date) return false;
      if (start && date < start) return false;
      if (end && date > end) return false;
      return true;
    });
  }, [alertasFiltradas, downloadEndDate, downloadPeriod, downloadStartDate]);

  const descargarHistorico = () => {
    const rows = exportRows.map((item) => ({
      "Hora inicio": formatDateTime(item.startAt),
      "Hora final": item.endAt ? formatDateTime(item.endAt) : "En curso",
      "Tiempo desabrochado": item.durationLabel,
      "Distancia recorrida": item.distanceLabel || formatDistance(item.distanceMeters),
      "Vehículo": item.vehiculoNombre || "Vehículo sin nombre",
      "Asiento": item.asiento || "Sin posición",
      "Evento": item.estado || "DESABROCHADO",
      "Severidad": normalizeSeverity(item.severidad),
      "Coordenada inicio": formatCoordinate(item.startLat, item.startLng),
      "Google Maps inicio": mapsUrl(item.startLat, item.startLng) || "",
      "Coordenada final": formatCoordinate(item.endLat, item.endLng),
      "Google Maps final": mapsUrl(item.endLat, item.endLng) || "",
      "Detalle": item.detalle || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ "Sin datos": "Sin datos para el periodo seleccionado" }]);
    worksheet["!cols"] = [
      { wch: 24 }, { wch: 24 }, { wch: 20 }, { wch: 20 }, { wch: 22 }, { wch: 18 },
      { wch: 18 }, { wch: 16 }, { wch: 24 }, { wch: 52 }, { wch: 24 }, { wch: 52 }, { wch: 60 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Histórico alertas");

    const suffix = downloadPeriod === "PERSONALIZADO" ? `${downloadStartDate || "inicio"}-${downloadEndDate || "fin"}` : downloadPeriod.toLowerCase();
    XLSX.writeFile(workbook, `historico-alertas-cinturones-${suffix}.xlsx`);
    setShowDownloadModal(false);
  };

  return (
    <div className="cinturones-alertas-page">
      <style>{`
        * { box-sizing: border-box; }
        .cinturones-alertas-page {
          min-height: 100vh;
          background: #071827;
          color: #ffffff;
          padding: 88px 18px 22px;
          font-family: Arial, Helvetica, sans-serif;
        }
        .alertas-wrap {
          width: 100%;
        }
        .alertas-card {
          background: linear-gradient(145deg, rgba(16, 38, 59, 0.96), rgba(8, 24, 40, 0.96));
          border: 1px solid rgba(93, 140, 183, 0.32);
          border-radius: 12px;
          padding: 22px;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.04);
        }
        .topbar {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 18px;
        }
        .top-actions {
          display: flex;
          gap: 10px;
          align-items: center;
          flex-wrap: wrap;
        }
        .download-btn,
        .modal-btn,
        .back-btn,
        .page-btn {
          border: 1px solid rgba(84, 154, 255, 0.55);
          background: rgba(17, 71, 123, 0.45);
          color: #ffffff;
          border-radius: 8px;
          padding: 10px 14px;
          cursor: pointer;
        }
        .download-btn {
          background: rgba(28, 117, 196, 0.62);
          border-color: rgba(125, 211, 252, 0.72);
          font-weight: 800;
        }
        .modal-btn.secondary {
          background: rgba(255,255,255,0.06);
          border-color: rgba(255,255,255,0.16);
        }
        .page-btn[disabled] {
          opacity: 0.45;
          cursor: default;
        }
        .title h1 {
          font-size: 24px;
          margin-bottom: 6px;
        }
        .title p {
          color: #d7e7f6;
          font-size: 14px;
        }
        .filters-row {
          display: grid;
          grid-template-columns: 200px 200px minmax(240px, 1fr);
          gap: 12px;
          margin-bottom: 16px;
        }
        .filter-control {
          width: 100%;
          border: 1px solid rgba(84, 154, 255, 0.28);
          background: rgba(6, 21, 35, 0.72);
          color: #ffffff;
          border-radius: 8px;
          padding: 11px 12px;
          outline: none;
        }
        .table-shell {
          overflow: auto;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 10px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          min-width: 1280px;
        }
        thead th {
          text-align: left;
          font-size: 12px;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          background: rgba(255,255,255,0.05);
          color: #ffffff;
          padding: 14px 12px;
        }
        tbody td {
          padding: 14px 12px;
          border-top: 1px solid rgba(255,255,255,0.08);
          color: #ffffff;
          font-size: 13px;
          vertical-align: top;
        }
        .badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 104px;
          padding: 7px 10px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
        }
        .badge.CRITICO {
          color: #ffb1b5;
          background: rgba(196, 41, 47, 0.25);
        }
        .badge.ADVERTENCIA {
          color: #ffd16a;
          background: rgba(188, 132, 24, 0.24);
        }
        .badge.OK {
          color: #9af0a8;
          background: rgba(40, 200, 50, 0.18);
        }
        .empty {
          padding: 26px 18px;
          text-align: center;
          color: #d7e7f6;
        }
        .map-link {
          color: #7cc7ff;
          font-weight: 700;
          text-decoration: none;
        }
        .map-link:hover {
          text-decoration: underline;
        }
        .time-stack {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .time-stack strong {
          color: #ffffff;
        }
        .time-stack span {
          color: #d7e7f6;
          font-size: 12px;
        }
        .download-modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 2400;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(2, 9, 16, 0.72);
          padding: 20px;
        }
        .download-modal {
          width: min(560px, 100%);
          background: linear-gradient(145deg, rgba(16, 38, 59, 0.98), rgba(8, 24, 40, 0.98));
          border: 1px solid rgba(93, 140, 183, 0.42);
          border-radius: 16px;
          padding: 22px;
          box-shadow: 0 24px 70px rgba(0,0,0,0.42);
        }
        .download-modal h2 {
          margin: 0 0 6px;
          font-size: 21px;
        }
        .download-modal p {
          color: #d7e7f6;
          margin: 0 0 18px;
          font-size: 13px;
        }
        .modal-grid {
          display: grid;
          gap: 14px;
        }
        .modal-field label {
          display: block;
          margin-bottom: 7px;
          color: #ffffff;
          font-size: 12px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .date-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 18px;
        }
        .footer-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          margin-top: 16px;
          flex-wrap: wrap;
        }
        .pager {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .meta {
          color: #d7e7f6;
          font-size: 13px;
        }
        @media (max-width: 900px) {
          .topbar,
          .download-modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 2400;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(2, 9, 16, 0.72);
          padding: 20px;
        }
        .download-modal {
          width: min(560px, 100%);
          background: linear-gradient(145deg, rgba(16, 38, 59, 0.98), rgba(8, 24, 40, 0.98));
          border: 1px solid rgba(93, 140, 183, 0.42);
          border-radius: 16px;
          padding: 22px;
          box-shadow: 0 24px 70px rgba(0,0,0,0.42);
        }
        .download-modal h2 {
          margin: 0 0 6px;
          font-size: 21px;
        }
        .download-modal p {
          color: #d7e7f6;
          margin: 0 0 18px;
          font-size: 13px;
        }
        .modal-grid {
          display: grid;
          gap: 14px;
        }
        .modal-field label {
          display: block;
          margin-bottom: 7px;
          color: #ffffff;
          font-size: 12px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .date-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 18px;
        }
        .footer-row {
            flex-direction: column;
            align-items: stretch;
          }
          .filters-row {
            grid-template-columns: 1fr;
          }
          .pager {
            justify-content: space-between;
          }
        }
      `}</style>

      <NavBar />

      <main className="alertas-wrap">
        <section className="alertas-card">
          <div className="topbar">
            <div className="title">
              <h1>Histórico de alertas</h1>
              <p>{vehiculoNombre || "Todos los vehículos"}</p>
            </div>
            <div className="top-actions">
              <button className="download-btn" type="button" onClick={() => setShowDownloadModal(true)}>Descargar</button>
              <button className="back-btn" type="button" onClick={() => navigate(-1)}>Retroceder</button>
            </div>
          </div>

          <div className="filters-row">
            <select className="filter-control" value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value)}>
              <option value="TODOS">Todos los estados</option>
              <option value="DESABROCHADO">Desabrochado</option>
              <option value="SIN_CINTURON">Sin cinturón</option>
            </select>

            <select className="filter-control" value={severidadFiltro} onChange={(e) => setSeveridadFiltro(e.target.value)}>
              <option value="TODAS">Todas las severidades</option>
              <option value="CRITICO">Crítico</option>
              <option value="ADVERTENCIA">Advertencia</option>
              <option value="OK">OK</option>
            </select>

            <input
              className="filter-control"
              type="text"
              placeholder="Buscar por vehículo, asiento o detalle"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>

          <div className="table-shell">
            <table>
              <thead>
                <tr>
                  <th>Hora inicio</th>
                  <th>Hora final</th>
                  <th>Tiempo desabrochado</th>
                  <th>Distancia recorrida</th>
                  <th>Vehículo</th>
                  <th>Asiento</th>
                  <th>Evento</th>
                  <th>Severidad</th>
                  <th>Coordenada inicio</th>
                  <th>Coordenada final</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((item) => {
                  const severity = normalizeSeverity(item.severidad);
                  const startMap = mapsUrl(item.startLat, item.startLng);
                  const endMap = mapsUrl(item.endLat, item.endLng);
                  return (
                    <tr key={item.id}>
                      <td>{formatDateTime(item.startAt)}</td>
                      <td>{item.endAt ? formatDateTime(item.endAt) : "En curso"}</td>
                      <td><strong>{item.durationLabel}</strong></td>
                      <td><strong>{item.distanceLabel || formatDistance(item.distanceMeters)}</strong></td>
                      <td>{item.vehiculoNombre || "Vehículo sin nombre"}</td>
                      <td>{item.asiento || "Sin posición"}</td>
                      <td>{item.estado || "DESABROCHADO"}</td>
                      <td><span className={`badge ${severity}`}>{severity}</span></td>
                      <td>
                        {startMap ? (
                          <a className="map-link" href={startMap} target="_blank" rel="noopener noreferrer">
                            {formatCoordinate(item.startLat, item.startLng)}
                          </a>
                        ) : "Sin coordenada"}
                      </td>
                      <td>
                        {endMap ? (
                          <a className="map-link" href={endMap} target="_blank" rel="noopener noreferrer">
                            {formatCoordinate(item.endLat, item.endLng)}
                          </a>
                        ) : "Sin coordenada"}
                      </td>
                    </tr>
                  );
                })}
                {visibleRows.length === 0 && (
                  <tr>
                    <td className="empty" colSpan="10">{loadingAlertas ? "Cargando alertas..." : "No hay alertas para los filtros seleccionados."}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="footer-row">
            <div className="meta">
              {eventTotal} alertas registradas{loadingAlertas ? " · cargando..." : ""}
            </div>
            <div className="pager">
              <button className="page-btn" type="button" disabled={page <= 1 || loadingAlertas} onClick={() => setPage((current) => Math.max(1, current - 1))}>Anterior</button>
              <span className="meta">Página {safePage} de {totalPages}</span>
              <button
                className="page-btn"
                type="button"
                disabled={page >= totalPages || loadingAlertas}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >Siguiente</button>
            </div>
          </div>
        </section>
      </main>

      {showDownloadModal && (
        <div className="download-modal-overlay" role="dialog" aria-modal="true" aria-label="Descargar histórico de alertas">
          <div className="download-modal">
            <h2>Descargar histórico de alertas</h2>
            <p>Exporta los eventos filtrados con tiempos, distancias y coordenadas.</p>

            <div className="modal-grid">
              <div className="modal-field">
                <label>Formato</label>
                <select className="filter-control" value={downloadFormat} onChange={(e) => setDownloadFormat(e.target.value)}>
                  <option value="XLSX">Excel (.xlsx)</option>
                </select>
              </div>

              <div className="modal-field">
                <label>Periodo</label>
                <select className="filter-control" value={downloadPeriod} onChange={(e) => setDownloadPeriod(e.target.value)}>
                  <option value="HOY">Hoy</option>
                  <option value="SEMANA">Semanal</option>
                  <option value="MES">Mes</option>
                  <option value="PERSONALIZADO">Personalizado</option>
                </select>
              </div>

              {downloadPeriod === "PERSONALIZADO" && (
                <div className="date-grid">
                  <div className="modal-field">
                    <label>Fecha inicio</label>
                    <input className="filter-control" type="date" value={downloadStartDate} onChange={(e) => setDownloadStartDate(e.target.value)} />
                  </div>
                  <div className="modal-field">
                    <label>Fecha fin</label>
                    <input className="filter-control" type="date" value={downloadEndDate} onChange={(e) => setDownloadEndDate(e.target.value)} />
                  </div>
                </div>
              )}
            </div>

            <div className="meta" style={{ marginTop: 14 }}>{exportRows.length} eventos se incluirán en la descarga.</div>

            <div className="modal-actions">
              <button className="modal-btn secondary" type="button" onClick={() => setShowDownloadModal(false)}>Cancelar</button>
              <button className="modal-btn" type="button" onClick={descargarHistorico}>Descargar Excel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
